/**
 * Shipping pricing, as pure functions.
 *
 * Everything here takes a cart and an address and returns a price. No database,
 * no injection, no tenancy — which is what makes eight pricing methods a
 * tractable amount of test surface instead of an integration-test problem.
 *
 * The design decision this file exists to enforce (see doc 25 §11): a store
 * configures **one method per delivery channel**, and the *address* decides the
 * channel. So no two methods ever compete to price the same order, there is no
 * precedence to configure, and "why did this customer pay ₹150" always has one
 * answer that can be traced.
 */

// ─── Kinds ───────────────────────────────────────────────────────────────────

/** Every way a store can price delivery. One per channel, never more. */
export const SHIPPING_KINDS = [
  'free', 'flat', 'order_value', 'per_item', 'distance', 'weight', 'zone_weight', 'courier_live',
] as const;
export type ShippingKind = (typeof SHIPPING_KINDS)[number];

export type ShippingChannelName = 'local' | 'outstation';

/**
 * Which methods are offered for which channel.
 *
 * Not every method means anything in every channel: kilometre bands stop
 * signifying at national range, and zoning a 5 km radius produces five zones
 * that are all the same zone. Offering them anyway would be a picker that
 * invites misconfiguration.
 */
export const KINDS_BY_CHANNEL: Record<ShippingChannelName, ShippingKind[]> = {
  local: ['free', 'flat', 'order_value', 'per_item', 'distance'],
  outstation: ['free', 'flat', 'order_value', 'per_item', 'weight', 'zone_weight', 'courier_live'],
};

// ─── India ───────────────────────────────────────────────────────────────────

/**
 * The eight metros, by pincode prefix.
 *
 * Prefixes rather than a city-name match, because "Bangalore" and "Bengaluru"
 * are the same place and a string comparison says otherwise. A pincode cannot
 * be spelled two ways.
 */
export const METRO_PINCODE_PREFIXES: Record<string, string> = {
  '110': 'Delhi NCR', '120': 'Delhi NCR', '121': 'Delhi NCR', '122': 'Delhi NCR', '201': 'Delhi NCR',
  '400': 'Mumbai', '401': 'Mumbai', '410': 'Mumbai',
  '700': 'Kolkata',
  '600': 'Chennai',
  '560': 'Bengaluru', '562': 'Bengaluru',
  '500': 'Hyderabad', '501': 'Hyderabad',
  '411': 'Pune', '412': 'Pune',
  '380': 'Ahmedabad', '382': 'Ahmedabad',
};

/**
 * Out of Delivery Area — where couriers levy a real surcharge.
 *
 * This is the zone merchants forget, and the one that quietly loses money: a
 * flat national rate absorbs the Aizawl surcharge on every order without
 * anybody noticing until the courier invoice arrives.
 */
export const ODA_STATES = [
  'arunachal pradesh', 'assam', 'manipur', 'meghalaya', 'mizoram', 'nagaland',
  'tripura', 'sikkim', 'jammu and kashmir', 'jammu & kashmir', 'ladakh',
  'andaman and nicobar islands', 'andaman & nicobar islands', 'lakshadweep',
];

export type ShippingZoneKind = 'local' | 'regional' | 'metro' | 'rest_of_india' | 'special';

/** Human labels, so admin and checkout never spell a zone differently. */
export const ZONE_LABELS: Record<ShippingZoneKind, string> = {
  local: 'Local',
  regional: 'Regional',
  metro: 'Metro',
  rest_of_india: 'Rest of India',
  special: 'Special / ODA',
};

const norm = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();

/**
 * Which courier zone an address falls in.
 *
 * Order matters and is not arbitrary — ODA is checked before Metro because a
 * Guwahati pincode is both "a city" and a surcharge area, and pricing it as a
 * metro is exactly the mistake that loses money.
 */
export function resolveZone(args: {
  originPincode?: string | null;
  originState?: string | null;
  originCity?: string | null;
  destPincode?: string | null;
  destState?: string | null;
  destCity?: string | null;
}): ShippingZoneKind {
  const destState = norm(args.destState);

  if (ODA_STATES.includes(destState)) return 'special';

  // Same pincode, or same city name — the shop's own town.
  const samePin = args.originPincode && args.destPincode && args.originPincode === args.destPincode;
  const sameCity = norm(args.originCity) && norm(args.originCity) === norm(args.destCity);
  if (samePin || sameCity) return 'local';

  if (norm(args.originState) && norm(args.originState) === destState) return 'regional';

  const prefix = (args.destPincode ?? '').slice(0, 3);
  if (METRO_PINCODE_PREFIXES[prefix]) return 'metro';

  return 'rest_of_india';
}

// ─── Distance ────────────────────────────────────────────────────────────────

export interface LatLng { latitude: number; longitude: number }

/**
 * Great-circle distance in kilometres.
 *
 * This is crow-flies. Real Indian city roads run about 1.35x longer, and far
 * worse across a river or a rail line — Hyderabad and Kolkata both have
 * neighbourhoods 2 km apart and 9 km by road. Callers pricing a journey should
 * apply {@link ROAD_DISTANCE_FACTOR} rather than treating this as travel
 * distance, or they under-charge on every single order.
 */
export function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Crow-flies to road, for Indian cities.
 *
 * A deliberate, documented constant rather than a number buried in a formula:
 * it is an assumption about geography, and the day someone wants to tune it
 * they should find it named.
 */
export const ROAD_DISTANCE_FACTOR = 1.35;

/** Road distance estimate — what a rider actually travels. */
export function roadDistanceKm(origin: LatLng, destination: LatLng): number {
  return haversineKm(origin, destination) * ROAD_DISTANCE_FACTOR;
}

// ─── Weight ──────────────────────────────────────────────────────────────────

export interface CartLine {
  quantity: number;
  /** Unit price in paise, after any per-line discount. */
  linePaise: number;
  weightGrams?: number | null;
}

/**
 * Billable weight for a cart.
 *
 * A product with no weight falls back to the store default rather than
 * contributing zero. A missing weight must never mean a free parcel — that is
 * a data gap silently becoming a discount.
 */
export function cartWeightGrams(lines: CartLine[], defaultWeightGrams: number): number {
  return lines.reduce(
    (total, line) => total + (line.weightGrams ?? defaultWeightGrams) * Math.max(line.quantity, 0),
    0
  );
}

/**
 * Slab pricing, rounded **up**, because couriers round up.
 *
 * `base` covers everything through `firstSlabGrams`; every started slab beyond
 * that costs `perSlabPaise`. Charging a proportional amount for a part-slab
 * would under-recover on every parcel that is not an exact multiple.
 */
export function slabPricePaise(args: {
  weightGrams: number;
  basePaise: number;
  firstSlabGrams: number;
  perSlabPaise: number;
  slabGrams: number;
}): number {
  const { weightGrams, basePaise, firstSlabGrams, perSlabPaise, slabGrams } = args;
  if (weightGrams <= firstSlabGrams || perSlabPaise <= 0 || slabGrams <= 0) return basePaise;

  const extra = Math.ceil((weightGrams - firstSlabGrams) / slabGrams);
  return basePaise + extra * perSlabPaise;
}

// ─── Configuration ───────────────────────────────────────────────────────────

export interface DistanceBand {
  uptoKm: number;
  basePaise: number;
  perKmPaise?: number;
  minMinutes?: number | null;
  maxMinutes?: number | null;
}

export interface ValueSlab { uptoPaise: number; pricePaise: number }
export interface WeightSlab { uptoGrams: number; pricePaise: number }

export interface ZoneRate {
  zone: ShippingZoneKind;
  basePaise: number;
  firstSlabGrams: number;
  perSlabPaise: number;
  slabGrams: number;
  minDays?: number | null;
  maxDays?: number | null;
}

/** One channel's configuration: its method, its data, and its modifiers. */
export interface ChannelConfig {
  channel: ShippingChannelName;
  kind: ShippingKind;
  name: string;
  basePaise?: number | null;

  bands?: DistanceBand[];
  valueSlabs?: ValueSlab[];
  weightSlabs?: WeightSlab[];
  zoneRates?: ZoneRate[];

  freeAbovePaise?: number | null;
  codEnabled?: boolean;
  codFlatPaise?: number;
  codPercentBps?: number;
  handlingPaise?: number;
}

export interface QuoteInput {
  lines: CartLine[];
  /** Cart total in paise, after discounts. */
  subtotalPaise: number;
  defaultWeightGrams: number;
  zone?: ShippingZoneKind;
  /** Road distance in km, when the channel prices by distance. */
  distanceKm?: number | null;
  cod?: boolean;
  /** A coupon has already granted free shipping. */
  freeShipping?: boolean;
}

export interface ShippingQuote {
  deliverable: boolean;
  /** Total the shopper pays for delivery, in paise, before GST. */
  amountPaise: number;
  /** Every step, in order, so "why this price" is always answerable. */
  reasons: string[];
  minDays?: number | null;
  maxDays?: number | null;
  minMinutes?: number | null;
  maxMinutes?: number | null;
  codAvailable: boolean;
  codFeePaise: number;
}

const rupees = (paise: number): string => `₹${(paise / 100).toFixed(2).replace(/\.00$/, '')}`;

// ─── The methods ─────────────────────────────────────────────────────────────

/**
 * Price one channel's method. Returns null when the method cannot price this
 * order at all — an address beyond every distance band, or a zone the merchant
 * never set a rate for.
 */
function priceMethod(
  config: ChannelConfig,
  input: QuoteInput,
  reasons: string[]
): { paise: number; minDays?: number | null; maxDays?: number | null; minMinutes?: number | null; maxMinutes?: number | null } | null {
  const base = config.basePaise ?? 0;

  switch (config.kind) {
    case 'free':
      reasons.push('Free delivery on this channel');
      return { paise: 0 };

    case 'flat':
      reasons.push(`Flat rate ${rupees(base)}`);
      return { paise: base };

    case 'per_item': {
      const units = input.lines.reduce((n, line) => n + Math.max(line.quantity, 0), 0);
      reasons.push(`${rupees(base)} × ${units} item${units === 1 ? '' : 's'}`);
      return { paise: base * units };
    }

    case 'order_value': {
      const slabs = [...(config.valueSlabs ?? [])].sort((a, b) => a.uptoPaise - b.uptoPaise);
      // The last slab is the open-ended one: anything above every threshold
      // pays it, so a merchant need not invent an upper bound for "and above".
      const slab = slabs.find(s => input.subtotalPaise <= s.uptoPaise) ?? slabs[slabs.length - 1];
      if (!slab) return null;

      reasons.push(`Cart ${rupees(input.subtotalPaise)} → ${rupees(slab.pricePaise)}`);
      return { paise: slab.pricePaise };
    }

    case 'weight': {
      const grams = cartWeightGrams(input.lines, input.defaultWeightGrams);
      const slabs = [...(config.weightSlabs ?? [])].sort((a, b) => a.uptoGrams - b.uptoGrams);
      const slab = slabs.find(s => grams <= s.uptoGrams) ?? slabs[slabs.length - 1];
      if (!slab) return null;

      reasons.push(`${(grams / 1000).toFixed(2)} kg → ${rupees(slab.pricePaise)}`);
      return { paise: slab.pricePaise };
    }

    case 'zone_weight': {
      const zone = input.zone ?? 'rest_of_india';
      const rate = (config.zoneRates ?? []).find(r => r.zone === zone);
      if (!rate) {
        reasons.push(`No rate set for ${ZONE_LABELS[zone]}`);
        return null;
      }

      const grams = cartWeightGrams(input.lines, input.defaultWeightGrams);
      const paise = slabPricePaise({
        weightGrams: grams,
        basePaise: rate.basePaise,
        firstSlabGrams: rate.firstSlabGrams,
        perSlabPaise: rate.perSlabPaise,
        slabGrams: rate.slabGrams,
      });

      reasons.push(`${ZONE_LABELS[zone]}, ${(grams / 1000).toFixed(2)} kg billed → ${rupees(paise)}`);
      return { paise, minDays: rate.minDays, maxDays: rate.maxDays };
    }

    case 'distance': {
      if (input.distanceKm == null) {
        reasons.push('Distance unknown for this address');
        return null;
      }

      const bands = [...(config.bands ?? [])].sort((a, b) => a.uptoKm - b.uptoKm);
      const band = bands.find(b => input.distanceKm! <= b.uptoKm);
      if (!band) {
        const furthest = bands[bands.length - 1];
        reasons.push(
          furthest
            ? `${input.distanceKm.toFixed(1)} km — beyond the ${furthest.uptoKm} km limit`
            : 'No delivery bands configured'
        );
        return null;
      }

      // Taper is measured from the previous band's edge, so a merchant who sets
      // one does not pay twice for the kilometres the base already covered.
      const previousEdge = bands[bands.indexOf(band) - 1]?.uptoKm ?? 0;
      const taper = band.perKmPaise
        ? Math.round(Math.max(input.distanceKm - previousEdge, 0) * band.perKmPaise)
        : 0;

      reasons.push(`${input.distanceKm.toFixed(1)} km → ${ZONE_LABELS['local']} band ${band.uptoKm} km, ${rupees(band.basePaise + taper)}`);
      return {
        paise: band.basePaise + taper,
        minMinutes: band.minMinutes,
        maxMinutes: band.maxMinutes,
      };
    }

    case 'courier_live':
      // The slot exists so adding it later is not a redesign (doc 25 §7).
      reasons.push('Live courier rates are not configured');
      return null;

    default:
      return null;
  }
}

/**
 * The full quote: method, then modifiers, in a fixed order.
 *
 * Order is load-bearing. Free-above is tested against the *cart*, not against
 * the shipping price, and handling is added before COD so a percentage COD fee
 * is charged on what the customer actually owes.
 */
export function quoteShipping(config: ChannelConfig, input: QuoteInput): ShippingQuote {
  const reasons: string[] = [];
  const priced = priceMethod(config, input, reasons);

  if (!priced) {
    return {
      deliverable: false, amountPaise: 0, reasons,
      codAvailable: false, codFeePaise: 0,
    };
  }

  let amount = priced.paise;

  if (config.handlingPaise) {
    amount += config.handlingPaise;
    reasons.push(`Handling ${rupees(config.handlingPaise)}`);
  }

  if (input.freeShipping) {
    amount = 0;
    reasons.push('Free shipping applied by coupon');
  } else if (config.freeAbovePaise != null && input.subtotalPaise >= config.freeAbovePaise) {
    amount = 0;
    reasons.push(`Free above ${rupees(config.freeAbovePaise)}`);
  }

  // COD is charged on top and is never waived by a free-shipping threshold —
  // the courier still charges the merchant for collecting cash.
  const codAvailable = config.codEnabled !== false;
  let codFeePaise = 0;

  if (input.cod && codAvailable) {
    const percent = Math.round((input.subtotalPaise * (config.codPercentBps ?? 0)) / 10000);
    codFeePaise = Math.max(config.codFlatPaise ?? 0, percent);
    if (codFeePaise > 0) reasons.push(`COD fee ${rupees(codFeePaise)}`);
  }

  return {
    deliverable: true,
    amountPaise: Math.max(amount, 0),
    reasons,
    minDays: priced.minDays ?? null,
    maxDays: priced.maxDays ?? null,
    minMinutes: priced.minMinutes ?? null,
    maxMinutes: priced.maxMinutes ?? null,
    codAvailable,
    codFeePaise,
  };
}

/**
 * Which channel an address belongs to.
 *
 * The address decides, never the shopper — this is the function that makes "one
 * method at a time" true, because a channel resolves to exactly one method and
 * there is nothing to rank.
 */
export function resolveChannel(args: {
  boundary: 'radius' | 'same_city' | 'same_state';
  localRadiusKm?: number | null;
  distanceKm?: number | null;
  originCity?: string | null;
  originState?: string | null;
  destCity?: string | null;
  destState?: string | null;
}): ShippingChannelName {
  switch (args.boundary) {
    case 'radius':
      // An unknown distance is Outstation, deliberately: pricing an unknown as
      // Local would hand a rider a delivery that might be 400 km away.
      return args.distanceKm != null && args.localRadiusKm != null && args.distanceKm <= args.localRadiusKm
        ? 'local'
        : 'outstation';

    case 'same_city':
      return norm(args.originCity) && norm(args.originCity) === norm(args.destCity)
        ? 'local'
        : 'outstation';

    case 'same_state':
      return norm(args.originState) && norm(args.originState) === norm(args.destState)
        ? 'local'
        : 'outstation';

    default:
      return 'outstation';
  }
}
